import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { HoslogPlans } from '@/services/hoslog-plans/hoslog-plans.types';
import * as React from 'react';
import useHoslogPlansStatus from '../hooks/useHoslogPlansStatus';
import HoslogPlansForm from '../HoslogPlansForm';
import { getColumns } from './Column';

export function HoslogPlansTable({
  data,
  pagination,
  isLoading = false,
}: {
  data: HoslogPlans[];
  pagination: Pagination;
  isLoading: boolean;
}) {
  const { mutateAsync: togglePlanStatus } = useHoslogPlansStatus();
  const { t } = useDynamicLocale();
  const { openConfirmation } = useConfirmationStore();

  // const { activeTab } = useSearch({ from: '/(app)/configuration-management/hoslog-plans' });

  const canAddNew = useCan({ allOf: [PERMS.plan.manage] });
  const { onOpen } = useDialogFormStore();
  const columns = React.useMemo(
    () => getColumns(onOpen, togglePlanStatus, t, openConfirmation),
    [onOpen, togglePlanStatus, t, openConfirmation],
  );

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      modalTitle: 'configurationManagement.plans.page.managePlansModal.hoslogPlans',
      title: 'Hoslog Plans',
      okText: t('common.page.modal.submit'),
      cancelText: t('common.page.button.cancel'),
      size: 'lg',
      component: <HoslogPlansForm />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    meta: {
      title: t('configurationManagement.plans.title'),
      totalPage: pagination.totalItems,
      actionKey: canAddNew ? 'onAddNew' : '',
      action: canAddNew ? () => onAddNew() : null,
      loading: isLoading,
    },
    getRowId: (originalRow) => String(originalRow.sno),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="font-medium rounded-t-none">
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
